import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import CharacterPicker from "@/components/CharacterPicker";

function fmtErr(d) {
 if (!d) return "Something went wrong.";
 if (typeof d === "string") return d;
 if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
 return d?.msg || String(d);
}

export function Login() {
 const { login } = useAuth();
 const nav = useNavigate();
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [err, setErr] = useState("");
 const [busy, setBusy] = useState(false);

 const submit = async (e) => {
 e.preventDefault();
 setErr(""); setBusy(true);
 try {
 await login(email, password);
 nav(new URLSearchParams(window.location.search).get("next") || "/");
 } catch (e) {
 setErr(fmtErr(e.response?.data?.detail) || e.message);
 } finally { setBusy(false); }
 };

 return (
 <AuthShell title="SIGN IN" subtitle="ENTER THE CYPHER">
 <form onSubmit={submit} className="space-y-5" data-testid="login-form">
 <Field label="EMAIL" type="email" value={email} onChange={setEmail} testId="login-email" />
 <Field label="PASSWORD" type="password" value={password} onChange={setPassword} testId="login-password" />
 {err && <div data-testid="login-error" className="font-mono text-xs text-yt-red border border-yt-red/40 bg-yt-red/5 p-3">{err}</div>}
 <button data-testid="login-submit" disabled={busy} className="w-full bg-yt-yellow text-black font-display text-lg tracking-wider py-3 hover:bg-white disabled:opacity-50 shadow-brutal shadow-brutal-hover transition-all">
 {busy ? "..." : "ENTER ▶"}
 </button>
 <div className="text-center text-sm text-yt-muted">
 No account? <Link to={`/register${window.location.search}`} data-testid="login-to-register" className="text-yt-yellow hover:underline">SIGN UP</Link>
 </div>
 </form>
 </AuthShell>
 );
}

export function Register() {
 const { register } = useAuth();
 const nav = useNavigate();
 // Capture ?ref= from the URL so we can credit whoever brought this user in.
 // Also read from localStorage in case the link was clicked earlier and the
 // user landed on other pages first before signing up.
 const initialRef = (() => {
   try {
     const urlRef = new URLSearchParams(window.location.search).get("ref");
     if (urlRef) { localStorage.setItem("yt_ref", urlRef); return urlRef; }
     return localStorage.getItem("yt_ref") || "";
   } catch (e) { return ""; }
 })();
 const [form, setForm] = useState({ email: "", password: "", rapper_name: "", style: "Boom Bap", gender: "male", ref: initialRef });
 const [err, setErr] = useState("");
 const [busy, setBusy] = useState(false);

 const submit = async (e) => {
 e.preventDefault();
 setErr(""); setBusy(true);
 try {
 await register(form);
 // Clear the referral cookie once used
 try { localStorage.removeItem("yt_ref"); } catch (e) {}
 nav(new URLSearchParams(window.location.search).get("next") || "/");
 } catch (e) {
 setErr(fmtErr(e.response?.data?.detail) || e.message);
 } finally { setBusy(false); }
 };

 const styles = ["Boom Bap", "Trap", "Conscious", "Multisyllabic", "Aggressive", "Smooth", "Outlaw Country Rap", "Drill"];

 return (
 <AuthShell title="CREATE PROFILE" subtitle="CLAIM YOUR ARENA" wide>
 <form onSubmit={submit} className="space-y-5" data-testid="register-form">
 {initialRef && (
   <div data-testid="register-ref-banner" className="border-2 border-yt-yellow bg-yt-yellow/10 p-4 text-center">
     <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-1">INVITED BY</div>
     <div className="font-display text-2xl tracking-widest text-white">@{initialRef}</div>
     <div className="font-mono text-[10px] text-yt-muted mt-2">They get credit when you sign up. Pay it forward.</div>
   </div>
 )}
 <Field label="RAPPER NAME" value={form.rapper_name} onChange={v => setForm({...form, rapper_name: v})} testId="register-rapper-name" />
 <Field label="EMAIL" type="email" value={form.email} onChange={v => setForm({...form, email: v})} testId="register-email" />
 <Field label="PASSWORD" type="password" value={form.password} onChange={v => setForm({...form, password: v})} testId="register-password" />
 <div className="bg-yt-surface/60 border border-yt-border p-3 text-sm text-yt-muted font-mono tracking-widest">
 <span className="text-yt-yellow">▸ NEXT STEP:</span> After signup, head to <span className="text-white">CHARACTER STUDIO</span> to generate your custom rapper portrait — full-body, on-stage, ready to spit.
 </div>
 <div>
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted mb-2">STYLE</div>
 <select data-testid="register-style" value={form.style} onChange={e => setForm({...form, style: e.target.value})}
 className="w-full bg-yt-surface border border-yt-border px-4 py-3 text-white font-body focus:border-yt-yellow focus:outline-none">
 {styles.map(s => <option key={s}>{s}</option>)}
 </select>
 </div>
 <div>
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted mb-2">VOCAL TYPE (WHAT VOICE YOUR RAPS COOK IN)</div>
 <div className="grid grid-cols-2 gap-2">
 {[{k:"male",l:"MALE"},{k:"female",l:"FEMALE"}].map(g => (
 <button type="button" key={g.k} data-testid={`register-gender-${g.k}`}
 onClick={() => setForm({...form, gender: g.k})}
 className={`py-3 border font-mono text-sm tracking-widest transition-colors ${
 form.gender === g.k ? "bg-yt-yellow text-black border-yt-yellow" : "border-yt-border text-white hover:border-yt-yellow"
 }`}>{g.l}</button>
 ))}
 </div>
 </div>
 <CharacterPicker value={form.portrait} onChange={(p) => setForm({...form, portrait: p})} testIdPrefix="register-char" />
 {err && <div data-testid="register-error" className="font-mono text-xs text-yt-red border border-yt-red/40 bg-yt-red/5 p-3">{err}</div>}
 <button data-testid="register-submit" disabled={busy} className="w-full bg-yt-yellow text-black font-display text-lg tracking-wider py-3 hover:bg-white disabled:opacity-50 shadow-brutal shadow-brutal-hover transition-all">
 {busy ? "..." : "CREATE & ENTER ▶"}
 </button>
 <div className="text-center text-sm text-yt-muted">
 Got an account? <Link to="/login" data-testid="register-to-login" className="text-yt-yellow hover:underline">SIGN IN</Link>
 </div>
 </form>
 </AuthShell>
 );
}

function AuthShell({ title, subtitle, children, wide }) {
 return (
 <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-6 py-12">
 <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"}`}>
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-2">{subtitle}</div>
 <h1 className="font-display text-5xl tracking-tight mb-8">{title}</h1>
 <div className="bg-yt-surface border border-yt-border p-8">
 {children}
 </div>
 </div>
 </div>
 );
}

function Field({ label, value, onChange, type = "text", testId }) {
 return (
 <div>
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted mb-2">{label}</div>
 <input
 data-testid={testId}
 type={type}
 value={value}
 onChange={e => onChange(e.target.value)}
 className="w-full bg-black border border-yt-border px-4 py-3 text-white font-body focus:border-yt-yellow focus:outline-none focus:ring-2 focus:ring-yt-yellow/20"
 required
 />
 </div>
 );
}
